import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import {
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  Box,
  Stack,
  Chip,
} from '@mui/material';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import NavigationOutlinedIcon from '@mui/icons-material/NavigationOutlined';
import PhoneIphoneOutlinedIcon from '@mui/icons-material/PhoneIphoneOutlined';
import MapIcon from '@mui/icons-material/Map';
import LoadingButton from '@mui/lab/LoadingButton';
import { useTheme } from '@mui/material/styles';
import handlePayment from './payment.js';
import { registerTransaction, getMerchantId } from './api.js';
import { useHookState } from './NotificationApiHook';
import { AuthContext } from './AuthContext';
import { PlaceContext } from '../contexts/PlaceContext';
import HallSchemaModal from './SchemaModal';

const ReservationModal = ({
  open,
  onClose,
  selectedBlocks,
  day,
  onReserve,
  balance,
  profileData,
  placeData,
}) => {
  const [selectedTables, setSelectedTables] = useState({});
  const theme = useTheme();
  const [load, setLoad] = useState(false);
  const [totalPrice, setTotalPrice] = useState(0);
  const [generateDaysQty, setGenerateDaysQty] = useState(0);
  const [dayAhead, setDayAhead] = useState(new Date());
  const sortBlocksById = (blocks) => {
    return blocks.slice().sort((a, b) => a.id - b.id);
  };
  const { isLoggedIn, token } = useContext(AuthContext);
  const isAdmin = localStorage.getItem('role_id') === '1';
  const sortedSelectedBlocks = sortBlocksById(selectedBlocks);
  const { notificationState } = useHookState();
  const { placeId } = useContext(PlaceContext);
  const [schemaModalOpen, setSchemaModalOpen] = useState(false);

  useEffect(() => {
    // Сброс состояния, когда окно закрыто
    if (!open) {
      setSelectedTables({});
      setGenerateDaysQty(0);
    }
  }, [open]);

  const isReserveButtonDisabled = () => {
    if (sortedSelectedBlocks.length === 0) {
      return true;
    }
    // Проверяем, есть ли для каждого блока выбранный стол
    for (const block of sortedSelectedBlocks) {
      if (!selectedTables[block.id]) {
        return true;
      }
    }
    return false;
  };

  const purchase = async (reservations) => {
    console.log('reserva', reservations);
    try {
      // For the admins we will not open payment page
      if (isAdmin) {
        if (generateDaysQty !== 0) {
          reservations = getReservationsForYear(day);
          console.log(reservations);
        }
        const orderData = await registerTransaction(reservations, token, notificationState);
        const { id: paymentId, order_id: orderId } = orderData;
        onReserve({ order_id: orderId, payment_id: paymentId, total_price: totalPrice });
      } else {
        const orderData = await registerTransaction(reservations, token, notificationState);
        const { id: paymentId, order_id: orderId, total_price: totalPrice } = orderData;
        // When money on balance is enougth
        if (totalPrice === 0) {
          onReserve({ order_id: orderId, payment_id: paymentId, total_price: totalPrice });
        } else {
          // Otherwise payment page will be opened.
          await getMerchantId(reservations[0].place_id, token, notificationState)
            .then((data) => {
              const merchantId = data;
              handlePayment(paymentId, totalPrice, orderId, 'Бронирование стола', merchantId)
                .then(() => {
                  onReserve({ order_id: orderId, payment_id: paymentId, total_price: totalPrice });
                })
                .catch(() => {
                  console.error('Failed while payment.');
                  onReserve({ order_id: orderId, payment_id: paymentId, total_price: totalPrice });
                });
            })
            .catch((error) => {
              console.error('Error fetching books for merchantId:', error);
            });
        }
      }
    } catch (error) {
      console.error('Ошибка при регистрации транзакции:', error);
    }
  };

  const formatDiscount = (discount) => {
    const multiplier = parseFloat(discount);
    const formattedDiscount =
      multiplier < 1
        ? `-${Math.round((1 - multiplier) * 100)}%`
        : `+${Math.round((multiplier - 1) * 100)}%`;

    return formattedDiscount;
  };

  const updateTotalPrice = () => {
    // Проверяем, есть ли выбранные блоки
    if (sortedSelectedBlocks.length === 0) {
      setTotalPrice(0);
      return;
    }

    // Суммируем цены выбранных столов
    const totalPrice = sortedSelectedBlocks.reduce((acc, block) => {
      // Получаем id стола для текущего блока времени из selectedTables
      const selectedTableId = selectedTables[block.id];

      // Если стол выбран, учитываем его цену
      if (selectedTableId) {
        const selectedTable = block.tables.find((table) => table.id === selectedTableId);
        if (selectedTable) {
          let total = 0;
          console.log(profileData);
          if (block.table_disq === 1) {
            const tableMultiplier = Number(selectedTable.disq);
            const tablePrice = block.price * tableMultiplier;
            total = tablePrice;
          } else total = Number(block.price);
          if (block.coach_disq === 1 && profileData.role_id === 3) {
            console.log(profileData, block.price);
            const coachDisq = block.price * Number(profileData.coach_disq);
            total = total - coachDisq;
          } else if (block.personal_disq === 1) {
            const personalDisq = block.price * Number(profileData.personal_disq);
            total = total - personalDisq;
          }
          return acc + total;
        }
      }

      return acc;
    }, 0);

    // Обновляем состояние с общей ценой
    setTotalPrice(totalPrice);
  };

  const getDatesForDayOfWeek = (chosenDay) => {
    const datesForDayOfWeek = [];
    let currentCheckDay = new Date(chosenDay);
    while (currentCheckDay <= dayAhead) {
      if (currentCheckDay.getDay() === chosenDay.getDay()) {
        datesForDayOfWeek.push(new Date(currentCheckDay));
      }
      currentCheckDay.setDate(currentCheckDay.getDate() + 7);
    }
    return datesForDayOfWeek;
  };

  const getReservationsForYear = (chosenDay) => {
    const reservationsForYear = [];
    const dayOfWeekDates = getDatesForDayOfWeek(chosenDay);
    for (const dateOfWeek of dayOfWeekDates) {
      for (const [blockId, tableId] of Object.entries(selectedTables)) {
        reservationsForYear.push({
          id: blockId,
          user_id: localStorage.user_id,
          place_id: placeId,
          table_id: tableId,
          day: dateOfWeek.toLocaleDateString('ru-RU'),
        });
      }
    }
    return reservationsForYear;
  };

  const handleSchemaModalOpen = () => {
    setSchemaModalOpen(true);
  };

  const handleReserveClick = async () => {
    try {
      let reservations = Object.entries(selectedTables).map(([blockId, tableId]) => ({
        id: blockId,
        user_id: localStorage.user_id,
        place_id: placeId,
        table_id: tableId,
        day: day.toLocaleDateString('ru-RU'),
      }));

      await purchase(reservations);
    } catch (error) {
      console.error('Error during purchase:', error);
    } finally {
      console.log(load);
      setLoad(false);
      onClose();
    }
  };

  useEffect(() => {
    updateTotalPrice();
  }, [selectedTables, sortedSelectedBlocks]);

  const handleChipClick = (daysQty) => {
    console.log('daysQty: ', daysQty);
    setGenerateDaysQty(daysQty);
    setDayAhead(new Date(day.getTime() + daysQty * 86400000)); // cast to milliseconds
    // dayAhead.setDate(day.getDate() + daysQty);
    console.log('day ahead: ', dayAhead, day);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='xs'
      fullWidth
      sx={{ zIndex: '99' }}
    >
      {placeData && (
        <DialogTitle
          sx={{
            background: 'linear-gradient(100deg, rgba(95,81,166,1) 81%, rgba(75,75,75,1) 19%);',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box width={300}>
              <Stack
                direction='column'
                sx={{
                  justifyContent: 'space-between',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '1.1rem',
                    fontWeight: '500',
                    marginBottom: '10px',
                    color: 'white',
                  }}
                >
                  {`${day.toLocaleDateString('ru-RU', {
                    month: 'long',
                    day: 'numeric',
                  })}`}{' '}
                  {`${day.toLocaleDateString('ru-RU', {
                    weekday: 'long',
                  })}
                  `}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '1.0625rem',
                    fontWeight: '600',
                    marginBottom: '10px',
                    color: 'white',
                  }}
                >
                  {`${placeData.name}`}
                </Typography>
              </Stack>
              <Stack
                direction='column'
                sx={{
                  justifyContent: 'space-between',
                }}
              >
                <Stack
                  direction='row'
                  alignItems='center'
                  gap={1}
                  mb={2}
                >
                  <NavigationOutlinedIcon sx={{ color: '#fff972' }} />
                  <Typography
                    sx={{
                      fontSize: '0.9rem',
                      color: 'white',
                    }}
                  >
                    {`${placeData.address}`}
                  </Typography>
                </Stack>
                <Stack
                  direction='row'
                  alignItems='center'
                  gap={1}
                  mb={2}
                >
                  <PhoneIphoneOutlinedIcon sx={{ color: '#fff972' }} />
                  <Typography
                    sx={{
                      fontSize: '0.9rem',
                      color: 'white',
                    }}
                  >{`${placeData.phone}`}</Typography>
                </Stack>
                <Stack
                  direction='row'
                  alignItems='center'
                  gap={1}
                  mb={2}
                >
                  <MapIcon sx={{ color: '#fff972' }} />
                  <Typography
                    onClick={handleSchemaModalOpen}
                    sx={{
                      fontSize: '0.9rem',
                      color: 'white',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                    }}
                  >{`Cхема зала`}</Typography>
                </Stack>
                <HallSchemaModal
                  open={schemaModalOpen}
                  onClose={() => setSchemaModalOpen(false)}
                  place_id={placeData.id}
                  token={token}
                  notificationState={notificationState}
                />
                {Number(balance) > 0 && (
                  <>
                    <Stack
                      direction='row'
                      alignItems='center'
                      gap={1}
                    >
                      <AccountBalanceWalletOutlinedIcon sx={{ color: '#A6D339' }} />
                      <Typography
                        sx={{
                          fontSize: '0.9rem',
                          color: 'white',
                        }}
                      >{`${profileData.balance}`}</Typography>
                    </Stack>
                  </>
                )}
              </Stack>
            </Box>
          </Box>
        </DialogTitle>
      )}
      <DialogContent sx={{ padding: '20px' }}>
        <Divider sx={{ mb: 1 }} />
        {sortedSelectedBlocks.map((block) => (
          <div
            key={block.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '16px',
            }}
          >
            <div style={{ flex: 1, textAlign: 'center', paddingTop: 11 }}>
              <Typography sx={{ fontSize: '0.9rem' }}>
                {`${block.start.toLocaleTimeString('ru-RU', {
                  hour: '2-digit',
                  minute: '2-digit',
                })} - ${block.end.toLocaleTimeString('ru-RU', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}`}
              </Typography>
            </div>
            <div style={{ flex: 1, marginTop: '10px' }}>
              <FormControl
                fullWidth
                variant='outlined'
                size='small'
              >
                <InputLabel>Выберите стол</InputLabel>
                <Select
                  label='Выберите стол'
                  value={selectedTables[block.id] || ''}
                  onChange={(e) =>
                    setSelectedTables((prev) => ({ ...prev, [block.id]: e.target.value }))
                  }
                >
                  {block.tables.map(
                    (table) =>
                      table.reserved === 0 && (
                        <MenuItem
                          key={table.id}
                          value={table.id}
                          sx={{ fontSize: '0.9rem' }}
                        >
                          {`Стол ${table.table_number} | ${table.type_name}${
                            block.table_disq !== 0 && table.disq !== '1'
                              ? ` | ${formatDiscount(table.disq)}`
                              : ''
                          }`}
                        </MenuItem>
                      ),
                  )}
                </Select>
              </FormControl>
            </div>
          </div>
        ))}
        <Divider sx={{ my: 2 }} />
        {profileData && (
          <>
            {profileData.coach_disq ? (
              <>
                <Typography sx={{ fontSize: '0.9rem', textAlign: 'center', width: '100%' }}>
                  {`Скидка тренера: ${profileData.coach_disq * 100}%`}
                </Typography>
                <Divider sx={{ my: 2 }} />
              </>
            ) : Number(profileData.personal_disq) > 0 ? (
              <>
                <Typography sx={{ fontSize: '0.9rem', textAlign: 'center', width: '100%' }}>
                  {`Персональная скидка: ${profileData.personal_disq * 100}%`}
                </Typography>
                <Divider sx={{ my: 2 }} />
              </>
            ) : null}
          </>
        )}
        {isAdmin && (
          <>
            <Typography
              mb={2}
              sx={{ fontSize: '0.9rem', textAlign: 'center', width: '100%' }}
            >
              Бронь вперед
            </Typography>
            <Stack
              direction='row'
              spacing={1}
              sx={{ display: 'flex', justifyContent: 'center' }}
            >
              <Chip
                label='месяц'
                variant={generateDaysQty === 30 ? 'filled' : 'outlined'}
                onClick={() => handleChipClick(30)}
                color='primary'
              />
              <Chip
                label='3 месяца'
                variant={generateDaysQty === 60 ? 'filled' : 'outlined'}
                onClick={() => handleChipClick(60)}
                color='primary'
              />
              <Chip
                label='год'
                variant={generateDaysQty === 365 ? 'filled' : 'outlined'}
                onClick={() => handleChipClick(365)}
                color='primary'
              />
              <Chip
                label='x'
                variant='outlined'
                onClick={() => handleChipClick(0)}
                color='primary'
              />
            </Stack>
            {generateDaysQty !== 0 && (
              <Typography
                my={2}
                sx={{ fontSize: '0.9rem', textAlign: 'center', width: '100%' }}
              >
                {`Забронируем до: ${dayAhead.toLocaleDateString('ru-RU', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
                `}
              </Typography>
            )}

            <Divider sx={{ my: 2 }} />
          </>
        )}

        <Typography sx={{ color: 'grey', fontSize: '10px', textAlign: 'center', width: '100%' }}>
          Перед оплатой, обратите внимание, что отмена брони с возвратом средств на баланс вашего
          профиля возможна не позже чем за 12 часов. <br></br>
          <Link
            to='/club-policy'
            style={{
              textAlign: 'center',
              textDecoration: 'underline',
              color: '#2B476A',
              marginBottom: 14,
              marginTop: 0,
            }}
          >
            Подробнее в правилах клуба
          </Link>
        </Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', textAlign: 'center', padding: 0 }}>
        {!load ? (
          <>
            <Button
              onClick={onClose}
              color='primary'
              sx={{ width: '25%' }}
            >
              Отмена
            </Button>
            <Button
              onClick={() => {
                setLoad(true);
                handleReserveClick();
              }}
              color='primary'
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                borderRadius: '0px',
                paddingTop: 2,
                paddingBottom: 2,
                width: '75%',
                '&.Mui-disabled': {
                  backgroundColor: '#4b4b4b',
                  color: theme.palette.primary.contrastText,
                },
                '&:hover': {
                  backgroundColor: '#8e80d7',
                },
              }}
              loadingPosition='end'
              variant='contained'
              disabled={isReserveButtonDisabled()}
            >
              Забронировать | {totalPrice} ₽
            </Button>
          </>
        ) : (
          <>
            <LoadingButton
              color='primary'
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                borderRadius: '0px',
                paddingTop: 4,
                paddingBottom: 3,
                width: '100%',
                '&.Mui-disabled': {
                  backgroundColor: '#fff',
                  color: theme.palette.primary.contrastText,
                },
                '&:hover': {
                  backgroundColor: '#8e80d7',
                },
              }}
              disabled
              variant='contained'
              loading={load}
            ></LoadingButton>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ReservationModal;
